import { useState, useEffect } from 'react';
import { useCurrentSubscription } from 'src/store/subscription';
import { useCurrentOrganization } from 'src/store/organization';
import { apiGetTrialDiscountEligibility } from 'src/utils/journeyApi';

interface UseTrialDiscountPopupReturn {
  shouldShowDiscountPopup: boolean;
  isDiscountPopupOpen: boolean;
  openDiscountPopup: () => void;
  closeDiscountPopup: () => void;
  daysSinceTrialExpiry: number;
}

export const useTrialDiscountPopup = (): UseTrialDiscountPopupReturn => {
  const { trialExpired, isSubscribed, isOnTrial } = useCurrentSubscription();
  const { currentOrganization } = useCurrentOrganization();
  const [isDiscountPopupOpen, setIsDiscountPopupOpen] = useState(false);
  const [daysSinceTrialExpiry, setDaysSinceTrialExpiry] = useState(0);
  const [hasCheckedEligibility, setHasCheckedEligibility] = useState(false);

  // Check eligibility only once when component mounts and organization is available
  useEffect(() => {
    const checkEligibility = async () => {
      if (!currentOrganization?.id || hasCheckedEligibility) return;

      try {
        const eligibility = await apiGetTrialDiscountEligibility(currentOrganization.id);
        
        if (eligibility.show_popup) {
          setDaysSinceTrialExpiry(eligibility.days_since_expiry);
          setIsDiscountPopupOpen(true);
        }
      } catch (error) {
        console.warn('Failed to check trial discount eligibility:', error);
      } finally {
        setHasCheckedEligibility(true);
      }
    };

    checkEligibility();
  }, [currentOrganization?.id, hasCheckedEligibility]);

  const openDiscountPopup = () => {
    setIsDiscountPopupOpen(true);
  };

  const closeDiscountPopup = () => {
    setIsDiscountPopupOpen(false);
  };

  const shouldShowDiscountPopup = 
    trialExpired && 
    !isSubscribed && 
    !isOnTrial && 
    daysSinceTrialExpiry >= 7;

  return {
    shouldShowDiscountPopup,
    isDiscountPopupOpen,
    openDiscountPopup,
    closeDiscountPopup,
    daysSinceTrialExpiry,
  };
};